import React, { useState, useEffect } from 'react';
import { Button, Card, Badge, Modal, Input } from '../../components/ui';
import { Users, Building as BuildingIcon, Shield, RefreshCw, Edit, Ban as BanIcon, CheckCircle as CheckCircleIcon, Search as SearchIcon, Loader2, User as UserIcon, Clock, Eye, Trash2, X } from 'lucide-react';
import apiService from '../../services/api';
import { User as UserType, StructuredLocation } from '../../types';
import SystemConfig from './SystemConfig';
import UserProfileHistory from '../../components/admin/UserProfileHistory';
import EmailTemplateEditor from '../../components/admin/EmailTemplateEditor';
import { useSkills } from '../../hooks/useSkills';
import { useAuth } from '../../contexts/AuthContext';
import CityAutocomplete from '../../components/location/CityAutocomplete';
import {
  PHONE_COUNTRY_CODE_OPTIONS,
  formatPhoneForDisplay,
  guessPhoneCountryCodeFromNumber
} from '../../utils/phone';

// Extended User interface for admin editing with all fields
interface ExtendedUser extends UserType {
  // Override specific fields that need different types for admin editing
  hasTicket?: boolean | null;
  hasVehiclePass?: boolean | null;
  userHistory?: any[];
}

interface AdminCampSummary {
  _id: string;
  name: string;
  slug?: string | null;
  isPubliclyVisible?: boolean;
  acceptingApplications?: boolean;
}

interface AdminMemberRecord {
  _id: string;
  camp: AdminCampSummary;
  status?: string | null;
  role?: string | null;
  signupSource?: string | null;
  isShiftsOnly?: boolean;
  name?: string;
  email?: string;
  playaName?: string;
  phone?: string;
  tags?: string[];
  skills?: string[];
  interests?: string[];
  customFieldValues?: Record<string, any>;
  applicationData?: Record<string, any>;
  appliedAt?: string | null;
  reviewedAt?: string | null;
  reviewNotes?: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

interface AdminSubmittedApplication {
  _id: string;
  camp: AdminCampSummary;
  status?: string | null;
  applicationData?: Record<string, any>;
  reviewNotes?: string;
  notes?: string;
  duesStatus?: string | null;
  appliedAt?: string | null;
  reviewedAt?: string | null;
  lastUpdated?: string | null;
  createdAt?: string | null;
}

interface AdminMemberDetail {
  user: UserType;
  memberships: AdminMemberRecord[];
  applications: AdminSubmittedApplication[];
}

interface DashboardStats {
  // All users (including camp and admin accounts)
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;

  // User accounts (individual users: personal + admin + unassigned, excludes camp accounts)
  totalPersonalUsers: number;
  activePersonalUsers: number;
  inactivePersonalUsers: number;
  
  // Camp statistics
  totalCamps: number;
  activeCamps: number;
  recruitingCamps: number;
  
  // Roster member statistics (member applications)
  activeRosterMembers: number;
  totalRosterMembers: number;
  pendingRosterMembers: number;
  
  // Legacy field (for backward compatibility)
  totalMembers: number;
  
  accountTypeStats: {
    personal: number;
    camp: number;
    admin: number;
    unassigned: number;
  };
  userGrowth: Array<{
    _id: { year: number; month: number };
    count: number;
  }>;
  campStats: Array<{
    _id: string;
    count: number;
  }>;
}

interface SystemHealth {
  totalAccounts: number;
  activeAccounts: number;
  inactiveAccounts: number;
  totalCamps: number;
  activeCamps: number;
  recruitingCamps: number;
  totalMembers: number;
  accountTypeBreakdown: {
    personal: number;
    camp: number;
    admin: number;
    unassigned: number;
  };
  recentActivity: {
    newUsers: number;
    newCamps: number;
  };
}

interface ActivityLog {
  action: string;
  adminId: string;
  adminName: string;
  targetId: string;
  targetName: string;
  changes: any;
  timestamp: string;
  type?: string;
}

interface AnalyticsData {
  period: string;
  userAnalytics: {
    total: number;
    active: number;
    retentionRate: string;
  };
  campAnalytics: {
    total: number;
    active: number;
    recruiting: number;
    new: number;
  };
  applicationStats: {
    total: number;
    new: number;
    pending: number;
    approved: number;
    rejected: number;
    approvalRate: string;
  };
  accountTypeDistribution: {
    personal: number;
    camp: number;
    admin: number;
    unassigned: number;
  };
  growthTrends: Array<{
    date: string;
    users: number;
    camps: number;
    applications: number;
  }>;
}

interface Camp {
  _id: string;
  name: string;
  campName?: string; // Keep for backward compatibility
  slug?: string;
  hometown?: string;
  location?: {
    city?: string;
    state?: string;
    country?: string;
    countryCode?: string;
    lat?: number;
    lng?: number;
    placeId?: string;
    street?: string;
    crossStreet?: string;
    time?: string;
    description?: string;
  };
  memberCount?: number;
  members?: any[];
  isActive: boolean;
  createdAt: string;
  description?: string;
  contactEmail?: string;
  website?: string;
  burningSince?: number;
  categories?: string[];
  selectedPerks?: any[];
  isPubliclyVisible?: boolean;
  acceptingApplications?: boolean;
  // New compact columns
  rosterInfo?: {
    hasActive: boolean;
    memberCount: number;
  };
  lastLogin?: string | Date;
  activeApps?: number;
  activeEvents?: number;
  activeShifts?: number;
  activeTasks?: number;
  owner?: string | { _id: string; firstName?: string; lastName?: string; email?: string; accountType?: 'personal' | 'camp' | 'admin' }; // Camp owner user ID
  ownerRef?: string | null;
  ownerLookupFailed?: boolean;
  socialMedia?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    tiktok?: string;
  };
}

const toStructuredLocationOrNull = (
  location?: Partial<StructuredLocation> | null
): StructuredLocation | null => {
  if (!location) return null;
  if (!location.city || !location.country || !location.countryCode) return null;
  if (location.lat === undefined || location.lng === undefined) return null;
  return {
    city: location.city,
    state: location.state,
    country: location.country,
    countryCode: location.countryCode,
    lat: Number(location.lat),
    lng: Number(location.lng),
    placeId: location.placeId
  };
};

const AdminDashboard: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    // All users (including camp and admin accounts)
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,

    // User accounts (individual users: personal + admin + unassigned, excludes camp accounts)
    totalPersonalUsers: 0,
    activePersonalUsers: 0,
    inactivePersonalUsers: 0,
    
    // Camp statistics
    totalCamps: 0,
    activeCamps: 0,
    recruitingCamps: 0,
    
    // Roster member statistics (member applications)
    activeRosterMembers: 0,
    totalRosterMembers: 0,
    pendingRosterMembers: 0,
    
    // Legacy field (for backward compatibility)
    totalMembers: 0,
    
    accountTypeStats: {
      personal: 0,
      camp: 0,
      admin: 0,
      unassigned: 0
    },
    userGrowth: [],
    campStats: []
  });
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [users, setUsers] = useState<UserType[]>([]);
  const [camps, setCamps] = useState<Camp[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(1); // Default to Camps (Members=0, Camps=1)
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedMemberDetail, setSelectedMemberDetail] = useState<AdminMemberDetail | null>(null);
  const [showMemberDetailModal, setShowMemberDetailModal] = useState(false);
  const [memberDetailLoadingId, setMemberDetailLoadingId] = useState<string | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedCamp, setSelectedCamp] = useState<Camp | null>(null);
  const [showCampModal, setShowCampModal] = useState(false);
  const [showDeleteCampModal, setShowDeleteCampModal] = useState(false);
  
  // Enhanced state for new features
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [auditLogs, setAuditLogs] = useState<ActivityLog[]>([]);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [selectedAccountType, setSelectedAccountType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedCamps, setSelectedCamps] = useState<string[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkAction, setBulkAction] = useState<string>('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [dateRange, setDateRange] = useState<string>('30d');
  const [activityHistory, setActivityHistory] = useState<ActivityLog[]>([]);
  const [showActivityHistory, setShowActivityHistory] = useState(false);
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [promoteTargetUser, setPromoteTargetUser] = useState<UserType | null>(null);
  const [promoteLoading, setPromoteLoading] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    // Reload camps when sort changes (only if already loaded)
    if (!loading && camps.length >= 0) {
      loadCamps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortOrder]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadStats(),
        loadUsers(),
        loadCamps(),
        loadAnalytics(),
        loadAuditLogs()
      ]);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await apiService.get(`/admin/analytics?period=${dateRange}`);
      setAnalytics(response);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const loadAuditLogs = async () => {
    try {
      const response = await apiService.get('/admin/audit-logs?limit=100');
      setAuditLogs(response.logs || []);
    } catch (error) {
      console.error('Error loading audit logs:', error);
    }
  };

  const loadUserHistory = async (userId: string) => {
    try {
      const response = await apiService.get(`/admin/users/${userId}/history`);
      setActivityHistory(response.activities || []);
      setShowActivityHistory(true);
    } catch (error) {
      console.error('Error loading user history:', error);
    }
  };

  const loadCampHistory = async (campId: string) => {
    try {
      const response = await apiService.get(`/admin/camps/${campId}/history`);
      setActivityHistory(response.activities || []);
      setShowActivityHistory(true);
    } catch (error) {
      console.error('Error loading camp history:', error);
    }
  };

  const handleBulkAction = async () => {
    // Determine which accounts are selected (users or camps)
    const totalSelected = selectedUsers.length + selectedCamps.length;
    if (!bulkAction || totalSelected === 0) return;

    // For camps, we need to get their owner user IDs
    let userIdsToProcess = [...selectedUsers];
    
    if (selectedCamps.length > 0) {
      // Extract owner user IDs from selected camps
      for (const campId of selectedCamps) {
        const camp = camps.find(c => c._id === campId);
        if (camp && camp.owner) {
          const ownerId = typeof camp.owner === 'object' ? camp.owner._id : camp.owner;
          if (ownerId && !userIdsToProcess.includes(ownerId)) {
            userIdsToProcess.push(ownerId);
          }
        }
      }
    }

    // Note: No additional browser confirmation needed - the modal already provides confirmation

    try {
      const response = await apiService.post('/admin/users/bulk-action', {
        action: bulkAction,
        userIds: userIdsToProcess,
        accountType: bulkAction === 'changeAccountType' ? selectedAccountType : undefined
      });

      console.log('Bulk action completed:', response);
      if (response?.errors?.length) {
        const failedIds = response.errors.map((item: any) => item.userId).join(', ');
        alert(`Bulk action partially completed.\nFailed ${response.errors.length} account(s): ${failedIds}`);
      }
      
      // Refresh data
      await loadUsers();
      await loadCamps();
      setSelectedUsers([]);
      setSelectedCamps([]);
      setShowBulkActions(false);
      setBulkAction('');
    } catch (error) {
      console.error('Error performing bulk action:', error);
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsers = () => {
    setSelectedUsers(users.map(user => user._id));
  };

  const clearSelection = () => {
    setSelectedUsers([]);
  };

  const toggleCampSelection = (campId: string) => {
    setSelectedCamps(prev => 
      prev.includes(campId) 
        ? prev.filter(id => id !== campId)
        : [...prev, campId]
    );
  };

  const selectAllCamps = () => {
    setSelectedCamps(filteredCamps.map(camp => camp._id));
  };

  const clearCampSelection = () => {
    setSelectedCamps([]);
  };

  const handlePromoteToSystemAdmin = async () => {
    if (!promoteTargetUser) return;
    try {
      setPromoteLoading(true);
      const response = await apiService.post(`/admin/users/${promoteTargetUser._id}/promote-to-system-admin`);
      const updated = response.user || { ...promoteTargetUser, isSystemAdmin: true };
      setUsers(prev => prev.map(u => u._id === promoteTargetUser._id ? updated : u));
      setPromoteTargetUser(null);
      alert(response.message || 'User has been promoted to System Admin.');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to promote user. Only system admins can perform this action.');
    } finally {
      setPromoteLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await apiService.get('/admin/dashboard');
      console.log('🔍 [AdminDashboard] Stats response:', response);
      // Ensure response.data exists and has the expected structure
      if (response && response.stats && typeof response.stats === 'object') {
        console.log('✅ [AdminDashboard] Setting stats:', response.stats);
        setStats({
          // All users (including camp/admin accounts)
          totalUsers: response.stats.totalUsers || 0,
          activeUsers: response.stats.activeUsers || 0,
          inactiveUsers: response.stats.inactiveUsers || 0,

          // User accounts (individual users: personal + admin + unassigned, excludes camp accounts)
          totalPersonalUsers: response.stats.totalPersonalUsers || 0,
          activePersonalUsers: response.stats.activePersonalUsers || 0,
          inactivePersonalUsers: response.stats.inactivePersonalUsers || 0,
          totalCamps: response.stats.totalCamps || 0,
          activeCamps: response.stats.activeCamps || 0,
          recruitingCamps: response.stats.recruitingCamps || 0,
          
          // Roster member statistics
          activeRosterMembers: response.stats.activeRosterMembers || 0,
          totalRosterMembers: response.stats.totalRosterMembers || 0,
          pendingRosterMembers: response.stats.pendingRosterMembers || 0,
          
          // Legacy field
          totalMembers: response.stats.totalMembers || 0,
          accountTypeStats: response.stats.accountTypeStats || {
            personal: 0,
            camp: 0,
            admin: 0,
            unassigned: 0
          },
          userGrowth: response.stats.userGrowth || [],
          campStats: response.stats.campStats || []
        });
      } else {
        console.log('❌ [AdminDashboard] Invalid stats response structure:', response);
      }

      if (response && response.systemHealth) {
        setSystemHealth(response.systemHealth);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
      // Keep the existing default stats on error - don't reset to undefined
    }
  };

  const loadUsers = async () => {
    try {
      // Request full list for System Admin (backend defaults to page=1, limit=20 otherwise)
      const response = await apiService.get('/admin/users?limit=1000');
      console.log('🔍 [AdminDashboard] Users response:', response);
      // Backend returns { data: users[], totalPages, currentPage, total }
      const userData = response.data ?? response.users;
      setUsers(Array.isArray(userData) ? userData : []);
    } catch (err) {
      console.error('Error loading users:', err);
      setUsers([]); // Ensure users is always an array
    }
  };

  const loadCamps = async () => {
    try {
      console.log(`🔄 [Admin UI] Loading camps with sort: ${sortField} ${sortOrder}`);
      // Request ALL camps (no pagination limit)
      const response = await apiService.get(`/admin/camps?sortBy=${sortField}&sortOrder=${sortOrder}&limit=1000`);
      console.log('✅ [Admin UI] Camps API response received:', { 
        total: response.total, 
        dataLength: response.data?.length,
        currentPage: response.currentPage 
      });
      
      // Backend returns { data: camps[], totalPages, currentPage, total }
      const campData = response.data || response.camps;
      console.log(`📊 [Admin UI] Processing ${campData?.length || 0} camps from response`);
      
      if (campData && Array.isArray(campData)) {
        const campNames = campData.map(c => c.name || c.campName);
        console.log('📋 [Admin UI] Camp names loaded:', campNames.slice(0, 10).join(', ') + (campNames.length > 10 ? '...' : ''));
      }
      
      setCamps(Array.isArray(campData) ? campData : []);
      console.log(`✅ [Admin UI] Camps state updated with ${campData?.length || 0} camps`);
    } catch (err) {
      console.error('❌ [Admin UI] Error loading camps:', err);
      console.error('❌ [Admin UI] Error response:', err.response?.data);
      setCamps([]); // Ensure camps is always an array
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleUserView = async (user: UserType) => {
    try {
      setMemberDetailLoadingId(user._id);
      const response = await apiService.get(`/admin/users/${user._id}/member-detail`);
      setSelectedMemberDetail(response as AdminMemberDetail);
      setShowMemberDetailModal(true);
    } catch (err: any) {
      console.error('Error loading member detail:', err);
      alert(err.response?.data?.message || 'Failed to load member details.');
    } finally {
      setMemberDetailLoadingId(null);
    }
  };

  const handleUserEdit = (user: UserType) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handleViewHistory = (userId: string) => {
    setSelectedUserId(userId);
    setShowHistoryModal(true);
  };

  const handleUserSave = async (updatedUser: UserType) => {
    try {
      const structuredLocation = toStructuredLocationOrNull(updatedUser.location);
      const payload: any = { ...updatedUser };
      const requestedNewPassword = payload.newPassword;
      delete payload.newPassword;
      delete payload.confirmPassword;
      delete payload.password;
      if (structuredLocation) {
        payload.location = structuredLocation;
        payload.city = structuredLocation.city;
      } else {
        delete payload.city;
        delete payload.location;
      }

      const response = await apiService.put(`/users/${updatedUser._id}`, payload);
      if (requestedNewPassword) {
        try {
          const passwordResponse = await apiService.put(`/users/${updatedUser._id}/password`, {
            newPassword: requestedNewPassword
          });
          if (passwordResponse?.passwordUpdated !== true) {
            throw new Error('Server did not verify the password update');
          }
        } catch (passwordError) {
          console.error('Profile saved but password update failed:', passwordError);
          alert('Profile changes were saved, but the password was not changed. Please try the password reset again.');
          return;
        }
      }

      // Update the user in the local state with the response from the server
      const savedUser = response.user || response.data?.user || updatedUser;
      setUsers(users.map(u => u._id === updatedUser._id ? savedUser : u));
      setShowUserModal(false);
      setSelectedUser(null);
      
      // Show success message
      alert(requestedNewPassword
        ? 'User updated successfully. The new password is effective immediately.'
        : 'User updated successfully! Changes will be reflected across all views.');
    } catch (err) {
      console.error('Error updating user:', err);
      alert('Failed to update user. Please try again.');
    }
  };

  const handleUserToggleActive = async (userId: string) => {
    try {
      const user = users.find(u => u._id === userId);
      if (user) {
        const updatedUser = { ...user, isActive: !user.isActive };
        const response = await apiService.put(`/users/${userId}`, updatedUser);
        const savedUser = response.user || response.data?.user || updatedUser;
        setUsers(users.map(u => u._id === userId ? savedUser : u));
      }
    } catch (err) {
      console.error('Error toggling user status:', err);
    }
  };

  const handleCampView = (camp: Camp) => {
    // Open camp in new tab
    const slug = camp.slug || camp._id;
    window.open(`/camps/public/${slug}`, '_blank');
  };

  const handleCampEdit = (camp: Camp) => {
    setSelectedCamp(camp);
    setShowCampModal(true);
  };

  const handleCampDelete = (camp: Camp) => {
    console.log(`🗑️ [Admin UI] Individual camp delete clicked: ${camp.name} (${camp._id})`);
    setSelectedCamp(camp);
    setShowDeleteCampModal(true);
  };

  const confirmDeleteCamp = async () => {
    if (!selectedCamp) return;

    try {
      console.log(`🗑️ [Admin UI] Deleting camp: ${selectedCamp.name} (${selectedCamp._id})`);
      const response = await apiService.delete(`/admin/camps/${selectedCamp._id}`);
      console.log(`✅ [Admin UI] Delete response:`, response);
      
      setShowDeleteCampModal(false);
      setSelectedCamp(null);
      
    // Refetch camps from server so UI always reflects backend state
    console.log(`🔄 [Admin UI] Refetching camps after deletion...`);
    await loadCamps();
    loadStats(); // Refresh stats
    console.log(`✅ [Admin UI] Camps refetched successfully`);

    // Verify the camp was actually deleted
    const stillExists = camps.find(c => c._id === selectedCamp._id);
    if (stillExists) {
      console.error(`❌ [Admin UI] Camp still exists after deletion: ${selectedCamp.name} (${selectedCamp._id})`);
      alert('Warning: Camp deletion may not have completed successfully. Please check the admin dashboard.');
    } else {
      console.log(`✅ [Admin UI] Verified camp removal from UI: ${selectedCamp.name} (${selectedCamp._id})`);
      alert('Camp deleted successfully!');
    }
    } catch (err) {
      console.error('❌ [Admin UI] Error deleting camp:', err);
      console.error('❌ [Admin UI] Error response:', err.response?.data);
      console.error('❌ [Admin UI] Error status:', err.response?.status);
      const message = err.response?.data?.message || err.message || 'Failed to delete camp. Please try again.';
      alert(message);
    }
  };

  const handleToggleAcceptingApplications = async (campId: string, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;
      console.log(`🔄 [Admin] Toggling acceptingApplications for camp ${campId} from ${currentStatus} to ${newStatus}`);
      
      const response = await apiService.put(`/admin/camps/${campId}/accepting-applications`, {
        acceptingApplications: newStatus
      });

      // Update local state
      setCamps(camps.map(c => 
        c._id === campId 
          ? { ...c, acceptingApplications: newStatus }
          : c
      ));

      console.log(`✅ [Admin] Successfully toggled acceptingApplications for camp ${campId}`);
    } catch (err) {
      console.error('❌ [Admin] Error toggling acceptingApplications:', err);
      alert('Failed to update application status. Please try again.');
    }
  };

  const handleTogglePubliclyVisible = async (campId: string, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;
      console.log(`🔄 [Admin] Toggling isPubliclyVisible for camp ${campId} from ${currentStatus} to ${newStatus}`);
      
      const response = await apiService.put(`/admin/camps/${campId}/publicly-visible`, {
        isPubliclyVisible: newStatus
      });

      // Update local state
      setCamps(camps.map(c => 
        c._id === campId 
          ? { ...c, isPubliclyVisible: newStatus }
          : c
      ));

      console.log(`✅ [Admin] Successfully toggled isPubliclyVisible for camp ${campId}`);
    } catch (err) {
      console.error('❌ [Admin] Error toggling isPubliclyVisible:', err);
      alert('Failed to update profile visibility. Please try again.');
    }
  };

  const handleCampSave = async (updatedCamp: Camp) => {
    try {
      const structuredLocation = toStructuredLocationOrNull(updatedCamp.location as StructuredLocation | null | undefined);
      const payload: any = { ...updatedCamp };
      if (structuredLocation) {
        payload.location = {
          ...(updatedCamp.location || {}),
          ...structuredLocation
        };
        payload.hometown = structuredLocation.city;
      } else {
        delete payload.hometown;
      }

      const response = await apiService.put(`/admin/camps/${updatedCamp._id}`, payload);
      const savedCamp = response.camp || response.data?.camp || updatedCamp;
      setCamps(camps.map(c => c._id === updatedCamp._id ? savedCamp : c));
      setShowCampModal(false);
      setSelectedCamp(null);
      alert('Camp updated successfully!');
    } catch (err) {
      console.error('Error updating camp:', err);
      alert('Failed to update camp. Please try again.');
    }
  };

  // Filter to show only personal/member accounts in Users tab (exclude camps and admins from user list)
  const filteredUsers = (users || []).filter(user => {
    const matchesSearch = (user.firstName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.lastName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    // Only show personal accounts in Users tab
    const isPersonalAccount = user.accountType === 'personal';
    
    return matchesSearch && isPersonalAccount;
  });

  const filteredCamps = (camps || []).filter(camp =>
    (camp.name || camp.campName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (camp.hometown && camp.hometown.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (camp.location?.city && camp.location.city.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-8 h-8 animate-spin text-custom-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-h1 font-lato-bold text-custom-text mb-2">
            Admin
          </h1>
          <p className="text-body text-custom-text-secondary">
            Manage users, camps, and system settings
          </p>
          
          {/* Stats Info Banner */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm">
            <div className="flex items-start gap-2">
              <div className="text-blue-600 mt-0.5">ℹ️</div>
              <div className="text-blue-800">
                <strong>Metrics Guide:</strong> 
                <span className="ml-2">
                  <strong>User Accounts</strong> = Individual accounts (personal + admin + unassigned, excludes camp accounts) • 
                  <strong>Roster Applications</strong> = Camp membership requests by status • 
                  <strong>Total Camps</strong> = Active camp profiles with valid owners
                </span>
              </div>
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={loadDashboardData}
          disabled={loading}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-custom-text-secondary">User Accounts</p>
              <p className="text-2xl font-lato-bold text-custom-text">{stats?.totalPersonalUsers || 0}</p>
              <div className="flex items-center gap-4 mt-2 text-xs">
                <span className="text-green-600">Active: {stats?.activePersonalUsers || 0}</span>
                <span className="text-red-600">Inactive: {stats?.inactivePersonalUsers || 0}</span>
              </div>
            </div>
            <Users className="w-8 h-8 text-custom-primary" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-custom-text-secondary">Total Camps</p>
              <p className="text-2xl font-lato-bold text-custom-text">{stats?.totalCamps || 0}</p>
              <div className="flex items-center gap-4 mt-2 text-xs">
                <span className="text-green-600">Active: {stats?.activeCamps || 0}</span>
                <span className="text-blue-600">Recruiting: {stats?.recruitingCamps || 0}</span>
              </div>
            </div>
            <BuildingIcon className="w-8 h-8 text-custom-primary" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-custom-text-secondary">Roster Applications</p>
              <p className="text-2xl font-lato-bold text-custom-text">{stats?.activeRosterMembers || 0}</p>
              <div className="flex items-center gap-4 mt-2 text-xs">
                <span className="text-green-600">Active: {stats?.activeRosterMembers || 0}</span>
                <span className="text-yellow-600">Pending: {stats?.pendingRosterMembers || 0}</span>
                <span className="text-gray-500">Total: {stats?.totalRosterMembers || 0}</span>
              </div>
            </div>
            <Shield className="w-8 h-8 text-custom-primary" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-custom-text-secondary">System Admins</p>
              <p className="text-2xl font-lato-bold text-custom-text">{stats?.accountTypeStats?.admin || 0}</p>
              <div className="mt-2 text-xs text-custom-text-secondary">
                Active admin accounts
              </div>
            </div>
            <Shield className="w-8 h-8 text-custom-primary" />
          </div>
        </Card>
      </div>


      {/* Navigation Tabs */}
      <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg overflow-x-auto">
        {[
          { id: 0, name: 'Members', icon: Users },
          { id: 1, name: 'Camps', icon: BuildingIcon },
          { id: 2, name: 'Email Templates', icon: Edit },
          { id: 3, name: 'System', icon: Shield }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-custom-primary shadow-sm'
                : 'text-custom-text-secondary hover:text-custom-text hover:bg-gray-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.name}
          </button>
        ))}
      </div>


      {/* Search - only show for Members and Camps tabs */}
      {(activeTab === 0 || activeTab === 1) && (
        <div className="mb-6">
          <div className="relative max-w-md">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search users or camps..."
              className="pl-10"
            />
          </div>
        </div>
      )}

      {/* Members Tab */}
      {activeTab === 0 && (
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-h2 font-lato-bold text-custom-text">
                Members Management
              </h2>
              <div className="flex items-center gap-2">
                {selectedUsers.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-custom-text-secondary">
                      {selectedUsers.length} selected
                    </span>
                    <Button 
                      onClick={() => setShowBulkActions(true)}
                      variant="outline"
                      size="sm"
                    >
                      Bulk Actions
                    </Button>
                    <Button 
                      onClick={clearSelection}
                      variant="outline"
                      size="sm"
                    >
                      Clear
                    </Button>
                  </div>
                )}
                <Button 
                  onClick={selectAllUsers}
                  variant="outline"
                  size="sm"
                >
                  Select All
                </Button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input 
                        type="checkbox" 
                        checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                        onChange={(e) => e.target.checked ? selectAllUsers() : clearSelection()}
                        className="rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input 
                          type="checkbox" 
                          checked={selectedUsers.includes(user._id)}
                          onChange={() => toggleUserSelection(user._id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                          <div className="text-xs text-gray-400 font-mono">ID: {user._id}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap items-center gap-1">
                          <Badge variant={user.accountType === 'admin' ? 'success' : user.accountType === 'camp' ? 'info' : 'neutral'}>
                            {user.accountType}
                          </Badge>
                          {(user.isSystemAdmin || (user.accountType === 'admin' && !user.campId)) && (
                            <Badge variant="success" className="bg-purple-600 text-white">
                              System Admin
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Badge variant={user.isActive ? 'success' : 'error'}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                          {user.campName && (
                            <Badge variant="info" className="text-xs">
                              {user.campName}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.lastLogin ? (
                          <div>
                            <div>{new Date(user.lastLogin).toLocaleDateString()}</div>
                            <div className="text-xs text-gray-400">
                              {new Date(user.lastLogin).toLocaleTimeString()}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">Never</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>
                          <div>{new Date(user.createdAt).toLocaleDateString()}</div>
                          <div className="text-xs text-gray-400">
                            {new Date(user.createdAt).toLocaleTimeString()}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUserView(user)}
                            disabled={memberDetailLoadingId === user._id}
                            className="flex items-center gap-1 text-blue-600 border-blue-600 hover:bg-blue-50"
                          >
                            {memberDetailLoadingId === user._id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Eye className="w-3 h-3" />
                            )}
                            {memberDetailLoadingId === user._id ? 'Loading' : 'View'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUserEdit(user)}
                            className="flex items-center gap-1"
                          >
                            <Edit className="w-3 h-3" />
                            Edit
                          </Button>
                          {currentUser?.isSystemAdmin && !((user.accountType === 'admin' && !user.campId) || user.isSystemAdmin) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPromoteTargetUser(user)}
                              className="flex items-center gap-1 text-purple-600 border-purple-600 hover:bg-purple-50"
                              title="Grant full system admin access"
                            >
                              <Shield className="w-3 h-3" />
                              Promote
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredUsers.length === 0 && (
              <div className="text-center py-12">
                <p className="text-custom-text-secondary">No users found</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Camps Tab */}
      {activeTab === 1 && (
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-h2 font-lato-bold text-custom-text">
                Camps Management
              </h2>
              <div className="flex items-center gap-2">
                {selectedCamps.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-custom-text-secondary">
                      {selectedCamps.length} selected
                    </span>
                    <Button 
                      onClick={() => setShowBulkActions(true)}
                      variant="outline"
                      size="sm"
                    >
                      Bulk Actions
                    </Button>
                    <Button 
                      onClick={clearCampSelection}
                      variant="outline"
                      size="sm"
                    >
                      Clear
                    </Button>
                  </div>
                )}
                <Button 
                  onClick={selectAllCamps}
                  variant="outline"
                  size="sm"
                >
                  Select All
                </Button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input 
                        type="checkbox" 
                        checked={selectedCamps.length === filteredCamps.length && filteredCamps.length > 0}
                        onChange={(e) => e.target.checked ? selectAllCamps() : clearCampSelection()}
                        className="rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('name')}>
                      Camp & User Info {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hometown
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('rosterInfo')}>
                      Roster {sortField === 'rosterInfo' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('lastLogin')}>
                      Last Login {sortField === 'lastLogin' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('activeApps')}>
                      Apps {sortField === 'activeApps' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('activeEvents')}>
                      Events {sortField === 'activeEvents' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('activeShifts')}>
                      Shifts {sortField === 'activeShifts' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('activeTasks')}>
                      Tasks {sortField === 'activeTasks' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Profile Visibility
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Applications
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('createdAt')}>
                      Created {sortField === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCamps.map((camp) => {
                    // Extract user info from camp owner
                    const ownerEmail = camp.owner && typeof camp.owner === 'object' 
                      ? camp.owner.email 
                      : camp.contactEmail || '';
                    const ownerFirstName = camp.owner && typeof camp.owner === 'object' 
                      ? camp.owner.firstName 
                      : '';
                    const ownerLastName = camp.owner && typeof camp.owner === 'object' 
                      ? camp.owner.lastName 
                      : '';
                    const ownerUserId = camp.owner && typeof camp.owner === 'object' 
                      ? camp.owner._id 
                      : (typeof camp.owner === 'string' ? camp.owner : (camp.ownerRef || ''));
                    
                    return (
                      <tr key={camp._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input 
                            type="checkbox" 
                            checked={selectedCamps.includes(camp._id)}
                            onChange={() => toggleCampSelection(camp._id)}
                            className="rounded"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {camp.name || camp.campName || '-'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {ownerFirstName && ownerLastName 
                                ? `${ownerFirstName} ${ownerLastName}` 
                                : (camp.ownerLookupFailed ? 'Owner lookup failed' : 'Unassigned')}
                            </div>
                            <div className="text-sm text-gray-500">{ownerEmail}</div>
                            <div className="text-xs text-gray-400 font-mono">
                              User ID: {ownerUserId || 'N/A'}
                            </div>
                          </div>
                        </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {camp.hometown || '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                        {camp.rosterInfo?.hasActive ? (
                          <span className="inline-flex items-center gap-1">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            {camp.rosterInfo.memberCount || 0}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                        {camp.lastLogin ? (() => {
                          try {
                            const date = new Date(camp.lastLogin);
                            if (!isNaN(date.getTime())) {
                              // Format as MM/DD/YYYY
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const day = String(date.getDate()).padStart(2, '0');
                              const year = date.getFullYear();
                              return (
                                <span className="text-xs" title={date.toLocaleString()}>
                                  {month}/{day}/{year}
                                </span>
                              );
                            }
                          } catch (e) {
                            console.warn('Invalid lastLogin date:', camp.lastLogin);
                          }
                          return <span className="text-gray-400">-</span>;
                        })() : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                        {camp.activeApps ?? '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                        {camp.activeEvents ?? '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                        {camp.activeShifts ?? '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                        {camp.activeTasks ?? '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleTogglePubliclyVisible(camp._id, camp.isPubliclyVisible !== false)}
                          className="flex items-center gap-2"
                        >
                          <Badge variant={camp.isPubliclyVisible !== false ? 'success' : 'neutral'}>
                            {camp.isPubliclyVisible !== false ? 'Public' : 'Private'}
                          </Badge>
                        </button>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleAcceptingApplications(camp._id, camp.acceptingApplications !== false)}
                          className="flex items-center gap-2"
                        >
                          <Badge variant={camp.acceptingApplications !== false ? 'success' : 'neutral'}>
                            {camp.acceptingApplications !== false ? 'Open' : 'Closed'}
                          </Badge>
                        </button>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <Badge variant={camp.isActive !== false ? 'success' : 'error'}>
                          {camp.isActive !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {camp.createdAt ? new Date(camp.createdAt).toLocaleDateString() : 'Invalid Date'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCampView(camp)}
                            className="flex items-center gap-1 text-blue-600 border-blue-600 hover:bg-blue-50"
                          >
                            <Eye className="w-3 h-3" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCampEdit(camp)}
                            className="flex items-center gap-1"
                          >
                            <Edit className="w-3 h-3" />
                            Edit
                          </Button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredCamps.length === 0 && (
              <div className="text-center py-12">
                <p className="text-custom-text-secondary">No camps found</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Configuration Tab */}
      {activeTab === 2 && (
        <SystemConfig />
      )}

      {/* FAQ Management Tab */}
      {activeTab === 3 && (
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-h2 font-lato-bold text-custom-text">
                FAQ Management
              </h2>
              <Button
                onClick={() => window.open('/help/admin', '_blank')}
                className="bg-custom-primary hover:bg-custom-primary-dark text-white"
              >
                Open FAQ Admin
              </Button>
            </div>
            <div className="text-custom-text-secondary">
              <p className="mb-4">
                Manage frequently asked questions for different user types. You can create, edit, and organize FAQs that will be displayed to users based on their account type.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2">FAQ Features:</h3>
                <ul className="text-blue-800 space-y-1">
                  <li>• Create and edit FAQs with categories</li>
                  <li>• Set display audience (Camps, Members, or Both)</li>
                  <li>• Control FAQ ordering and active status</li>
                  <li>• Automatic filtering based on user account type</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Analytics Tab - DEPRECATED */}
      {false && (
        <div className="space-y-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-h2 font-lato-bold text-custom-text">
                System Analytics
              </h2>
              <div className="flex items-center gap-2">
                <select 
                  value={dateRange} 
                  onChange={(e) => setDateRange(e.target.value)}
                  className="px-3 py-1 border rounded-md text-sm"
                >
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                  <option value="1y">Last year</option>
                </select>
                <Button onClick={loadAnalytics} variant="outline" size="sm">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {analytics ? (
              <div className="space-y-6">
                {/* User Analytics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-2">User Retention</h3>
                    <p className="text-2xl font-bold text-blue-600">{analytics.userAnalytics.retentionRate}%</p>
                    <p className="text-sm text-blue-700">
                      {analytics.userAnalytics.active} of {analytics.userAnalytics.total} users active
                    </p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h3 className="font-semibold text-green-900 mb-2">Camp Activity</h3>
                    <p className="text-2xl font-bold text-green-600">{analytics.campAnalytics.recruiting}</p>
                    <p className="text-sm text-green-700">Currently recruiting</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h3 className="font-semibold text-purple-900 mb-2">Application Rate</h3>
                    <p className="text-2xl font-bold text-purple-600">{analytics.applicationStats.approvalRate}%</p>
                    <p className="text-sm text-purple-700">Approval rate</p>
                  </div>
                </div>

                {/* Account Type Distribution */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Account Type Distribution</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(analytics.accountTypeDistribution).map(([type, count]) => (
                      <div key={type} className="text-center p-3 bg-gray-50 rounded">
                        <p className="text-2xl font-bold text-custom-text">{count}</p>
                        <p className="text-sm text-custom-text-secondary capitalize">{type}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Growth Trends */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Growth Trends (Last 30 Days)</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Date</th>
                          <th className="text-right py-2">Users</th>
                          <th className="text-right py-2">Camps</th>
                          <th className="text-right py-2">Applications</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.growthTrends.slice(-10).map((trend, index) => (
                          <tr key={index} className="border-b">
                            <td className="py-2">{trend.date}</td>
                            <td className="text-right py-2">{trend.users}</td>
                            <td className="text-right py-2">{trend.camps}</td>
                            <td className="text-right py-2">{trend.applications}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-custom-primary" />
                <p className="text-custom-text-secondary">Loading analytics...</p>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Audit Logs Tab - DEPRECATED */}
      {false && (
        <div className="space-y-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-h2 font-lato-bold text-custom-text">
                Audit Logs
              </h2>
              <div className="flex items-center gap-2">
                <Button onClick={loadAuditLogs} variant="outline" size="sm">
                  <RefreshCw className="w-4 h-4" />
                </Button>
                <Button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} variant="outline" size="sm">
                  <SearchIcon className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {showAdvancedFilters && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold mb-3">Advanced Filters</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Action</label>
                    <select className="w-full px-3 py-1 border rounded-md text-sm">
                      <option value="">All Actions</option>
                      <option value="user_updated">User Updated</option>
                      <option value="camp_updated">Camp Updated</option>
                      <option value="bulk_activate">Bulk Activate</option>
                      <option value="bulk_deactivate">Bulk Deactivate</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Target Type</label>
                    <select className="w-full px-3 py-1 border rounded-md text-sm">
                      <option value="">All Types</option>
                      <option value="user">User</option>
                      <option value="camp">Camp</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Start Date</label>
                    <input type="date" className="w-full px-3 py-1 border rounded-md text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">End Date</label>
                    <input type="date" className="w-full px-3 py-1 border rounded-md text-sm" />
                  </div>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Timestamp</th>
                    <th className="text-left py-2">Action</th>
                    <th className="text-left py-2">Admin</th>
                    <th className="text-left py-2">Target</th>
                    <th className="text-left py-2">Changes</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-2">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="py-2">
                        <Badge variant="info" className="text-xs">
                          {log.action}
                        </Badge>
                      </td>
                      <td className="py-2">{log.adminName}</td>
                      <td className="py-2">{log.targetName}</td>
                      <td className="py-2">
                        <code className="text-xs bg-gray-100 px-1 rounded">
                          {JSON.stringify(log.changes).slice(0, 50)}...
                        </code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* Email Templates Tab */}
      {activeTab === 2 && (
        <EmailTemplateEditor />
      )}

      {/* System Tab */}
      {activeTab === 3 && (
        <SystemConfig />
      )}

      {/* Bulk Actions Modal */}
      {showBulkActions && (
        <Modal isOpen={showBulkActions} onClose={() => setShowBulkActions(false)}>
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Bulk Actions</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Action</label>
                <select 
                  value={bulkAction} 
                  onChange={(e) => setBulkAction(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Select an action</option>
                  <option value="activate">Activate Users</option>
                  <option value="deactivate">Deactivate Users</option>
                  <option value="changeAccountType">Change Account Type</option>
                  <option value="delete">Permanently Delete</option>
                </select>
              </div>
              
              {bulkAction === 'changeAccountType' && (
                <div>
                  <label className="block text-sm font-medium mb-2">New Account Type</label>
                  <select 
                    value={selectedAccountType} 
                    onChange={(e) => setSelectedAccountType(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="personal">Personal</option>
                    <option value="camp">Camp</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              )}
              
              <div className={`${bulkAction === 'delete' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'} border rounded-lg p-4`}>
                <p className={`text-sm ${bulkAction === 'delete' ? 'text-red-800 font-semibold' : 'text-yellow-800'}`}>
                  {bulkAction === 'delete' ? (
                    <>
                      ⚠️ <strong>PERMANENT DELETION WARNING:</strong> This action will <strong>IRREVERSIBLY DELETE</strong> {selectedUsers.length + selectedCamps.length} account(s).
                      {selectedUsers.length > 0 && <><br />• {selectedUsers.length} member account(s)</>}
                      {selectedCamps.length > 0 && <><br />• {selectedCamps.length} camp account(s)</>}
                      <br /><br />
                      • CAMP accounts: User deleted, camp data and all related entities deleted
                      <br />
                      • MEMBER accounts: User deleted, tasks transferred to camps
                    </>
                  ) : (
                    <>
                      This action will affect <strong>{selectedUsers.length + selectedCamps.length}</strong> selected account(s).
                      {selectedUsers.length > 0 && <><br />• {selectedUsers.length} member account(s)</>}
                      {selectedCamps.length > 0 && <><br />• {selectedCamps.length} camp account(s)</>}
                    </>
                  )}
                </p>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowBulkActions(false)}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleBulkAction}
                  disabled={!bulkAction || (selectedUsers.length === 0 && selectedCamps.length === 0)}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Execute Action
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Activity History Modal */}
      {showActivityHistory && (
        <Modal isOpen={showActivityHistory} onClose={() => setShowActivityHistory(false)}>
          <div className="p-6 max-w-4xl">
            <h2 className="text-xl font-semibold mb-4">Activity History</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Timestamp</th>
                    <th className="text-left py-2">Action</th>
                    <th className="text-left py-2">Admin</th>
                    <th className="text-left py-2">Changes</th>
                  </tr>
                </thead>
                <tbody>
                  {activityHistory.map((activity, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-2">
                        {new Date(activity.timestamp).toLocaleString()}
                      </td>
                      <td className="py-2">
                        <Badge variant="info" className="text-xs">
                          {activity.action}
                        </Badge>
                      </td>
                      <td className="py-2">{activity.adminName}</td>
                      <td className="py-2">
                        <code className="text-xs bg-gray-100 px-1 rounded">
                          {JSON.stringify(activity.changes).slice(0, 100)}...
                        </code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Modal>
      )}

      {/* Member Detail Modal */}
      {showMemberDetailModal && selectedMemberDetail && (
        <AdminMemberDetailModal
          detail={selectedMemberDetail}
          onClose={() => {
            setShowMemberDetailModal(false);
            setSelectedMemberDetail(null);
          }}
        />
      )}

      {/* User Edit Modal */}
      {showUserModal && selectedUser && (
        <UserEditModal
          user={selectedUser}
          onClose={() => {
            setShowUserModal(false);
            setSelectedUser(null);
          }}
          onSave={handleUserSave}
        />
      )}

      {/* User History Modal */}
      {showHistoryModal && selectedUserId && (
        <UserProfileHistory
          userId={selectedUserId}
          isOpen={showHistoryModal}
          onClose={() => {
            setShowHistoryModal(false);
            setSelectedUserId(null);
          }}
        />
      )}

      {/* Camp Edit Modal */}
      {showCampModal && selectedCamp && (
        <CampEditModal
          camp={selectedCamp}
          onClose={() => {
            setShowCampModal(false);
            setSelectedCamp(null);
          }}
          onSave={handleCampSave}
        />
      )}

      {/* Camp Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteCampModal}
        onClose={() => {
          setShowDeleteCampModal(false);
          setSelectedCamp(null);
        }}
        title="Delete Camp"
      >
        {selectedCamp && (
          <div className="space-y-4">
            <p className="text-custom-text">
              Are you sure you want to delete <strong>{selectedCamp.name}</strong>? 
              This action cannot be undone and will remove all associated data.
            </p>
            <div className="flex gap-3 pt-4">
              <Button
                onClick={confirmDeleteCamp}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Delete Camp
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteCampModal(false);
                  setSelectedCamp(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Promote to System Admin Confirmation Modal */}
      <Modal
        isOpen={!!promoteTargetUser}
        onClose={() => !promoteLoading && setPromoteTargetUser(null)}
        title="Promote to System Admin"
        size="md"
      >
        {promoteTargetUser && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800 font-medium mb-2">
                Are you sure you want to grant full system access?
              </p>
              <p className="text-sm text-gray-700">
                <strong>{promoteTargetUser.firstName} {promoteTargetUser.lastName}</strong> ({promoteTargetUser.email})
                will be able to access the admin dashboard, manage all camps and users, and impersonate accounts.
              </p>
            </div>
            <p className="text-sm text-gray-600">
              This action is audited. Existing roles (Camp Lead, Roster Member) and camp associations are preserved.
            </p>
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setPromoteTargetUser(null)}
                className="flex-1"
                disabled={promoteLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handlePromoteToSystemAdmin}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                disabled={promoteLoading}
              >
                {promoteLoading ? 'Promoting...' : 'Promote to System Admin'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// Enhanced User Edit Modal Component with Full SystemAdmin Controls
// Impersonate Button Component
// Supports both member accounts (personal) and camp accounts
const ImpersonateButton: React.FC<{
  userId: string;
  userEmail: string;
  userName: string;
  accountType?: 'personal' | 'camp' | 'admin';
  campId?: string;
  needsRepair?: boolean;
}> = ({ userId, userEmail, userName, accountType, campId, needsRepair = false }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleImpersonate = async () => {
    try {
      setLoading(true);
      const response = await apiService.post('/admin/impersonate', {
        targetUserId: userId === 'camp-repair-needed' ? undefined : userId,
        campId: campId
      });

      if (response.url) {
        // Open in new window (incognito-compatible)
        window.open(response.url, '_blank', 'noopener,noreferrer');
        setShowConfirm(false);
        alert(`Opening login session for ${userName} in a new window. The link will expire in 5 minutes.`);
      }
    } catch (error: any) {
      console.error('Impersonation error:', error);
      alert(error.response?.data?.message || 'Failed to generate impersonation link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setShowConfirm(true)}
        className={`w-full flex items-center justify-center gap-2 ${needsRepair ? 'border-yellow-400 text-yellow-700 hover:bg-yellow-50' : 'border-orange-300 text-orange-700 hover:bg-orange-50'}`}
        disabled={loading}
        title={needsRepair ? 'Will attempt to repair camp owner link before impersonation' : 'Log in as this user'}
      >
        <span className="mr-2">{needsRepair ? '🔧' : '🔐'}</span>
        {loading ? 'Generating...' : (needsRepair ? 'Repair & Log in' : 'Log in as this user')}
      </Button>

      {showConfirm && (
        <Modal
          isOpen={showConfirm}
          onClose={() => setShowConfirm(false)}
          title="Confirm Impersonation"
          size="md"
        >
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800 mb-2">
                <strong>⚠️ Warning:</strong> You are about to log in as:
              </p>
              <p className="text-sm font-semibold text-gray-900">
                {userName} ({userEmail})
              </p>
              {accountType && (
                <p className="text-xs text-gray-600 mt-1">
                  Account Type: {accountType === 'camp' ? 'Camp Account' : accountType === 'personal' ? 'Member Account' : 'Admin Account'}
                </p>
              )}
              {needsRepair && (
                <p className="text-xs text-yellow-700 mt-2 font-medium">
                  ⚠️ This camp's owner link will be automatically repaired before impersonation.
                </p>
              )}
            </div>
            <div className="text-sm text-gray-700 space-y-2">
              <p>• A new window will open with this user's session</p>
              <p>• The impersonation link expires in 5 minutes</p>
              <p>• This action will be logged in the audit trail</p>
              <p>• Use this feature responsibly for support and debugging</p>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowConfirm(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleImpersonate}
                className="flex-1"
                disabled={loading}
              >
                {loading ? 'Generating...' : 'Continue'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

const formatAdminDate = (value?: string | Date | null): string => {
  if (!value) return 'Not provided';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not provided';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatBooleanDisplay = (value?: boolean | null): string => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return 'Not informed';
};

const formatFieldLabel = (key: string): string =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDetailValue = (value: any): string => {
  if (value === null || value === undefined || value === '') return 'Not provided';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.map(formatDetailValue).join(', ') : 'Not provided';
  if (typeof value === 'object') {
    const entries = Object.entries(value).filter(([, nestedValue]) => (
      nestedValue !== null &&
      nestedValue !== undefined &&
      nestedValue !== '' &&
      !(Array.isArray(nestedValue) && nestedValue.length === 0)
    ));
    if (entries.length === 0) return 'Not provided';
    return entries.map(([nestedKey, nestedValue]) => `${formatFieldLabel(nestedKey)}: ${formatDetailValue(nestedValue)}`).join('; ');
  }
  return String(value);
};

const getSocialUrl = (platform: 'instagram' | 'facebook' | 'linkedin', value?: string): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  const cleaned = raw.replace(/^@/, '');
  if (platform === 'instagram') return `https://instagram.com/${cleaned}`;
  if (platform === 'facebook') return `https://facebook.com/${cleaned}`;
  return `https://linkedin.com/in/${cleaned}`;
};

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => {
  const hasValue = value !== null && value !== undefined && value !== '';

  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-sm text-gray-900 break-words">{hasValue ? value : 'Not provided'}</div>
    </div>
  );
};

const DataObjectGrid: React.FC<{ data?: Record<string, any> }> = ({ data }) => {
  const entries = Object.entries(data || {}).filter(([, value]) => (
    value !== null &&
    value !== undefined &&
    value !== '' &&
    !(Array.isArray(value) && value.length === 0)
  ));

  if (entries.length === 0) {
    return <p className="text-sm text-gray-500">No submitted field data.</p>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {entries.map(([key, value]) => (
        <DetailField
          key={key}
          label={formatFieldLabel(key)}
          value={formatDetailValue(value)}
        />
      ))}
    </div>
  );
};

const AdminMemberDetailModal: React.FC<{
  detail: AdminMemberDetail;
  onClose: () => void;
}> = ({ detail, onClose }) => {
  const { user, memberships, applications } = detail;
  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
  const city = user.location?.city || user.city || 'Not provided';
  const socialLinks = [
    { label: 'Instagram', value: user.socialMedia?.instagram, url: getSocialUrl('instagram', user.socialMedia?.instagram) },
    { label: 'Facebook', value: user.socialMedia?.facebook, url: getSocialUrl('facebook', user.socialMedia?.facebook) },
    { label: 'LinkedIn', value: user.socialMedia?.linkedin, url: getSocialUrl('linkedin', user.socialMedia?.linkedin) }
  ].filter((link) => link.value && link.url);

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Member View: ${fullName}`}
      size="lg"
    >
      <div className="max-h-[80vh] overflow-y-auto space-y-6">
        <div className="flex items-start gap-4 rounded-lg border bg-gray-50 p-4">
          <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-full bg-gray-200 flex items-center justify-center">
            {user.profilePhoto ? (
              <img src={user.profilePhoto} alt={fullName} className="h-full w-full object-cover" />
            ) : (
              <UserIcon className="h-8 w-8 text-gray-400" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-xl font-semibold text-gray-900">{fullName}</h3>
              <Badge variant={user.isActive ? 'success' : 'error'}>
                {user.isActive ? 'Active' : 'Inactive'}
              </Badge>
              <Badge variant="neutral">{user.accountType}</Badge>
            </div>
            {user.playaName && (
              <p className="mt-1 text-sm font-medium text-orange-600">"{user.playaName}"</p>
            )}
            <p className="mt-1 text-sm text-gray-600">{user.email}</p>
            <p className="mt-1 text-xs text-gray-500 font-mono">ID: {user._id}</p>
          </div>
        </div>

        <div className="rounded-lg border bg-white p-4">
          <h3 className="text-lg font-medium text-custom-text mb-4">Profile Data</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <DetailField label="First Name" value={user.firstName || 'Not provided'} />
            <DetailField label="Last Name" value={user.lastName || 'Not provided'} />
            <DetailField label="Playa Name" value={user.playaName || 'Not provided'} />
            <DetailField label="City" value={city} />
            <DetailField label="Region" value={user.location?.state || 'Not provided'} />
            <DetailField label="Country" value={user.location?.country || 'Not provided'} />
            <DetailField label="Phone" value={formatPhoneForDisplay(user.phoneNumber, user.phoneCountryCode) || 'Not provided'} />
            <DetailField label="Years Burned" value={user.yearsBurned ?? 'Not provided'} />
            <DetailField label="Burning Plans" value={user.burningPlans || 'Not provided'} />
            <DetailField label="Ticket" value={formatBooleanDisplay(user.hasTicket)} />
            <DetailField label="Vehicle Pass" value={formatBooleanDisplay(user.hasVehiclePass)} />
            <DetailField label="Food Preferences" value={user.foodPreferences?.length ? user.foodPreferences.join(', ') : 'Not provided'} />
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <DetailField label="Arrival Date" value={formatAdminDate(user.arrivalDate)} />
            <DetailField label="Departure Date" value={formatAdminDate(user.departureDate)} />
            <DetailField label="Early Arrival Interest" value={formatBooleanDisplay(user.interestedInEAP)} />
            <DetailField label="Strike Interest" value={formatBooleanDisplay(user.interestedInStrike)} />
          </div>

          <div className="mt-4">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Social Media</div>
            {socialLinks.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {socialLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded border border-blue-200 px-3 py-1 text-sm text-blue-700 hover:bg-blue-50"
                  >
                    {link.label}: {link.value}
                  </a>
                ))}
              </div>
            ) : (
              <p className="mt-1 text-sm text-gray-500">Not provided</p>
            )}
          </div>

          {user.bio && (
            <div className="mt-4">
              <DetailField label="Bio" value={user.bio} />
            </div>
          )}
        </div>

        <div className="rounded-lg border bg-white p-4">
          <h3 className="text-lg font-medium text-custom-text mb-4">Camp Membership Records</h3>
          {memberships.length === 0 ? (
            <p className="text-sm text-gray-500">No camp membership records found.</p>
          ) : (
            <div className="space-y-4">
              {memberships.map((member) => (
                <div key={member._id} className="rounded-lg border border-gray-200 p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <div className="font-semibold text-gray-900">{member.camp?.name || 'Unknown camp'}</div>
                    {member.status && <Badge variant="neutral">{member.status}</Badge>}
                    {member.role && <Badge variant="info">{member.role}</Badge>}
                    {member.signupSource && <Badge variant="neutral">{member.signupSource}</Badge>}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <DetailField label="Roster Name" value={member.name || 'Not provided'} />
                    <DetailField label="Roster Email" value={member.email || 'Not provided'} />
                    <DetailField label="Roster Playa Name" value={member.playaName || 'Not provided'} />
                    <DetailField label="Roster Phone" value={member.phone || 'Not provided'} />
                    <DetailField label="Applied At" value={formatAdminDate(member.appliedAt)} />
                    <DetailField label="Tags" value={member.tags?.length ? member.tags.join(', ') : 'Not provided'} />
                  </div>
                  {member.applicationData && Object.keys(member.applicationData).length > 0 && (
                    <div className="mt-4 border-t pt-4">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3">Membership Submitted Data</h4>
                      <DataObjectGrid data={member.applicationData} />
                    </div>
                  )}
                  {member.reviewNotes && (
                    <div className="mt-4">
                      <DetailField label="Review Notes" value={member.reviewNotes} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-lg border bg-white p-4">
          <h3 className="text-lg font-medium text-custom-text mb-4">Applications Submitted to Camps</h3>
          {applications.length === 0 ? (
            <p className="text-sm text-gray-500">No submitted applications found.</p>
          ) : (
            <div className="space-y-4">
              {applications.map((application) => (
                <div key={application._id} className="rounded-lg border border-gray-200 p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <div className="font-semibold text-gray-900">{application.camp?.name || 'Unknown camp'}</div>
                    {application.status && <Badge variant="info">{application.status}</Badge>}
                    {application.duesStatus && <Badge variant="neutral">Dues: {application.duesStatus}</Badge>}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    <DetailField label="Applied At" value={formatAdminDate(application.appliedAt)} />
                    <DetailField label="Reviewed At" value={formatAdminDate(application.reviewedAt)} />
                    <DetailField label="Last Updated" value={formatAdminDate(application.lastUpdated)} />
                  </div>
                  <DataObjectGrid data={application.applicationData} />
                  {(application.reviewNotes || application.notes) && (
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                      {application.reviewNotes && <DetailField label="Review Notes" value={application.reviewNotes} />}
                      {application.notes && <DetailField label="Notes" value={application.notes} />}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end border-t pt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const UserEditModal: React.FC<{
  user: UserType;
  onClose: () => void;
  onSave: (user: UserType) => void;
}> = ({ user, onClose, onSave }) => {
  const { skills: availableSkills, loading: skillsLoading } = useSkills();
  const [formData, setFormData] = useState<ExtendedUser>({
    ...user,
    phoneCountryCode: user.phoneCountryCode || guessPhoneCountryCodeFromNumber(user.phoneNumber) || '+1',
    skills: user.skills || [],
    location: toStructuredLocationOrNull(user.location) || undefined
  } as ExtendedUser);
  const [legacyCity] = useState<string>(user.city || '');
  const [photoPreview, setPhotoPreview] = useState<string | null>(user.profilePhoto || null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [auditLog, setAuditLog] = useState<any[]>([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Load audit log when modal opens
  useEffect(() => {
    loadAuditLog();
  }, [user._id]);

  const loadAuditLog = async () => {
    try {
      setAuditLogLoading(true);
      const response = await apiService.get(`/admin/users/${user._id}/history`);
      setAuditLog(response.activities || []);
    } catch (error) {
      console.error('Error loading audit log:', error);
      setAuditLog([]);
    } finally {
      setAuditLogLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (newPassword && newPassword !== confirmPassword) {
        alert('Passwords do not match');
        return;
      }

      if (newPassword && newPassword.length < 6) {
        alert('New password must be at least 6 characters');
        return;
      }

      const saveData: ExtendedUser = { ...formData };
      if (newPassword) {
        (saveData as any).newPassword = newPassword;
      }

      // Photo data is already in formData from handlePhotoUpload/handlePhotoDelete
      // Just validate and save
      console.log('💾 [AdminDashboard] Saving user with photo:', formData.profilePhoto ? 'Photo included' : 'No photo');
      onSave(saveData as UserType);
    } catch (err) {
      console.error('Error preparing user data:', err);
      alert('Failed to prepare user data. Please try again.');
    }
  };

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Result = e.target?.result as string;
        setPhotoPreview(base64Result);
        // Update formData immediately so it's ready for save
        setFormData(prev => ({ ...prev, profilePhoto: base64Result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoDelete = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setFormData({ ...formData, profilePhoto: '' });
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`SystemAdmin: Edit User ${formData._id}`}
      size="lg"
    >
      <div className="space-y-6 max-h-[80vh] overflow-y-auto">
        {/* Profile Photo Management */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="block text-label font-medium text-custom-text mb-3">
            Profile Photo Management
          </label>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
              {photoPreview ? (
                <img src={photoPreview} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <UserIcon className="w-8 h-8 text-gray-400" />
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                Upload Photo
              </Button>
              {photoPreview && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePhotoDelete}
                  className="text-red-600 border-red-600 hover:bg-red-50"
                >
                  Delete Photo
                </Button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
            />
          </div>
        </div>

        {/* Basic Information */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="text-lg font-medium text-custom-text mb-4">Basic Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                User ID (UID)
              </label>
              <Input
                value={formData._id}
                disabled
                className="bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                Email
              </label>
              <Input
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                First Name
              </label>
              <Input
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                Last Name
              </label>
              <Input
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Account Settings */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="text-lg font-medium text-custom-text mb-4">Account Settings</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                Account Type
              </label>
              <select
                value={formData.accountType}
                onChange={(e) => setFormData({ ...formData, accountType: e.target.value as any })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-primary focus:border-transparent"
              >
                <option value="personal">Personal</option>
                <option value="camp">Camp</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                Camp Affiliation
              </label>
              <Input
                value={formData.campName || ''}
                onChange={(e) => setFormData({ ...formData, campName: e.target.value })}
                placeholder="Current camp (if any)"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="rounded border-gray-300 text-custom-primary focus:ring-custom-primary"
              />
              <label className="text-sm text-custom-text">Active</label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.isVerified}
                onChange={(e) => setFormData({ ...formData, isVerified: e.target.checked })}
                className="rounded border-gray-300 text-custom-primary focus:ring-custom-primary"
              />
              <label className="text-sm text-custom-text">Verified</label>
            </div>
          </div>
        </div>

        {/* Password Reset */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="text-lg font-medium text-custom-text mb-2">Reset Password</h3>
          <p className="text-sm text-gray-600 mb-4">Leave blank to keep current password</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                New Password
              </label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>
            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                Confirm Password
              </label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
              )}
            </div>
          </div>
        </div>

        {/* Personal Details */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="text-lg font-medium text-custom-text mb-4">Personal Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                Phone
              </label>
              <div className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-2">
                <Input
                  value={formData.phoneCountryCode || ''}
                  onChange={(e) => setFormData({ ...formData, phoneCountryCode: e.target.value })}
                  placeholder="+1"
                  list="admin-user-phone-country-codes"
                />
                <Input
                  value={formData.phoneNumber || ''}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  placeholder="Phone number"
                />
                <datalist id="admin-user-phone-country-codes">
                  {PHONE_COUNTRY_CODE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </datalist>
              </div>
            </div>
            <div>
              <CityAutocomplete
                value={toStructuredLocationOrNull(formData.location)}
                onChange={(location) => setFormData({
                  ...formData,
                  location: location || undefined
                })}
                label="Location"
                placeholder="Search and select city"
                legacyValue={legacyCity}
              />
            </div>
            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                Playa Name
              </label>
              <Input
                value={formData.playaName || ''}
                onChange={(e) => setFormData({ ...formData, playaName: e.target.value })}
                placeholder="Burner name"
              />
            </div>
            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                Years Burned
              </label>
              <Input
                type="number"
                value={formData.yearsBurned || 0}
                onChange={(e) => setFormData({ ...formData, yearsBurned: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                Previous Camps
              </label>
              <Input
                value={formData.previousCamps || ''}
                onChange={(e) => setFormData({ ...formData, previousCamps: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                State / Region
              </label>
              <Input
                value={formData.location?.state || ''}
                disabled
                className="bg-gray-100"
                placeholder="Auto-filled from selected city"
              />
            </div>
            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                Country
              </label>
              <Input
                value={formData.location?.country || ''}
                disabled
                className="bg-gray-100"
                placeholder="Auto-filled from selected city"
              />
            </div>
          </div>
          
          <div className="mt-4">
            <label className="block text-label font-medium text-custom-text mb-2">
              Bio
            </label>
            <textarea
              value={formData.bio || ''}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              rows={3}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-primary focus:border-transparent"
            />
          </div>

          <div className="mt-4">
            <label className="block text-label font-medium text-custom-text mb-2">
              Years Burned {formData.yearsBurned === 0 && <span className="text-purple-600 font-semibold ml-2">(Virgin)</span>}
            </label>
            <p className="text-sm text-gray-600 mb-2">
              {formData.yearsBurned === 0 ? 'This user is a Burning Man virgin (0 years)' : `This user has attended Burning Man for ${formData.yearsBurned} year${formData.yearsBurned !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="text-lg font-medium text-custom-text mb-4">Social Media</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                Instagram
              </label>
              <Input
                value={formData.socialMedia?.instagram || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  socialMedia: { ...formData.socialMedia, instagram: e.target.value }
                })}
                placeholder="@username"
              />
            </div>
            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                Facebook
              </label>
              <Input
                value={formData.socialMedia?.facebook || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  socialMedia: { ...formData.socialMedia, facebook: e.target.value }
                })}
                placeholder="Facebook URL or username"
              />
            </div>
            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                LinkedIn
              </label>
              <Input
                value={formData.socialMedia?.linkedin || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  socialMedia: { ...formData.socialMedia, linkedin: e.target.value }
                })}
                placeholder="LinkedIn URL or username"
              />
            </div>
          </div>
        </div>

        {/* Ticket and Vehicle Pass Status */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="text-lg font-medium text-custom-text mb-4">Ticket & Vehicle Pass Status</h3>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-label font-medium text-custom-text mb-3">
                Has Ticket
              </label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="hasTicket"
                    value="true"
                    checked={formData.hasTicket === true}
                    onChange={() => setFormData({ ...formData, hasTicket: true })}
                    className="text-custom-primary focus:ring-custom-primary"
                  />
                  <span className="text-sm text-custom-text">Yes</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="hasTicket"
                    value="false"
                    checked={formData.hasTicket === false}
                    onChange={() => setFormData({ ...formData, hasTicket: false })}
                    className="text-custom-primary focus:ring-custom-primary"
                  />
                  <span className="text-sm text-custom-text">No</span>
                </label>
              </div>
            </div>
            <div>
              <label className="block text-label font-medium text-custom-text mb-3">
                Has VP
              </label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="hasVehiclePass"
                    value="true"
                    checked={formData.hasVehiclePass === true}
                    onChange={() => setFormData({ ...formData, hasVehiclePass: true })}
                    className="text-custom-primary focus:ring-custom-primary"
                  />
                  <span className="text-sm text-custom-text">Yes</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="hasVehiclePass"
                    value="false"
                    checked={formData.hasVehiclePass === false}
                    onChange={() => setFormData({ ...formData, hasVehiclePass: false })}
                    className="text-custom-primary focus:ring-custom-primary"
                  />
                  <span className="text-sm text-custom-text">No</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Travel Plans */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="text-lg font-medium text-custom-text mb-4">Travel Plans</h3>
          <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-label font-medium text-custom-text mb-2">
                    Arrival Date
                  </label>
                  <Input
                    type="date"
                    value={formData.arrivalDate ? (typeof formData.arrivalDate === 'string' ? formData.arrivalDate : formData.arrivalDate.toISOString().split('T')[0]) : ''}
                    onChange={(e) => setFormData({ ...formData, arrivalDate: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-label font-medium text-custom-text mb-2">
                    Departure Date
                  </label>
                  <Input
                    type="date"
                    value={formData.departureDate ? (typeof formData.departureDate === 'string' ? formData.departureDate : formData.departureDate.toISOString().split('T')[0]) : ''}
                    onChange={(e) => setFormData({ ...formData, departureDate: e.target.value })}
                  />
                </div>
          </div>
        </div>

        {/* Volunteering Interests */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="text-lg font-medium text-custom-text mb-4">Volunteering Interests</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.interestedInEAP || false}
                onChange={(e) => setFormData({ ...formData, interestedInEAP: e.target.checked })}
                className="rounded border-gray-300 text-custom-primary focus:ring-custom-primary"
              />
              <label className="text-sm text-custom-text">Interested in Early Arrival (EA)</label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.interestedInStrike || false}
                onChange={(e) => setFormData({ ...formData, interestedInStrike: e.target.checked })}
                className="rounded border-gray-300 text-custom-primary focus:ring-custom-primary"
              />
              <label className="text-sm text-custom-text">Interested in Strike/Load-out</label>
            </div>
          </div>
        </div>

        {/* Camp Bio (for camp accounts) */}
        {formData.accountType === 'camp' && (
          <div className="bg-white border rounded-lg p-4">
            <h3 className="text-lg font-medium text-custom-text mb-4">Camp Information</h3>
            <div className="mt-4">
              <label className="block text-label font-medium text-custom-text mb-2">
                Camp Bio
              </label>
              <textarea
                value={formData.campBio || ''}
                onChange={(e) => setFormData({ ...formData, campBio: e.target.value })}
                rows={4}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-primary focus:border-transparent"
                placeholder="Describe the camp's mission, activities, and what makes it special..."
              />
            </div>
          </div>
        )}

        {/* Skills & Interests */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="text-lg font-medium text-custom-text mb-4">Skills & Interests</h3>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 mb-4">
              {formData.skills?.map((skill, index) => (
                <div key={index} className="flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                  <span>{skill}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const newSkills = formData.skills?.filter((_, i) => i !== index) || [];
                      setFormData({ ...formData, skills: newSkills });
                    }}
                    className="hover:bg-green-200 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                Select Skills
              </label>
              <select
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-primary focus:border-transparent text-gray-900"
                value=""
                onChange={(e) => {
                  const skill = e.target.value;
                  if (skill && !formData.skills?.includes(skill)) {
                    setFormData({ ...formData, skills: [...(formData.skills || []), skill].sort() });
                  }
                  // Reset dropdown
                  e.target.value = '';
                }}
                disabled={skillsLoading}
              >
                <option value="">{skillsLoading ? 'Loading skills...' : 'Choose a skill to add...'}</option>
                {availableSkills.map((skill) => (
                  <option key={skill} value={skill}>{skill}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Account Deactivation Control */}
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-red-900 mb-1">
                Account Status
              </h3>
              <p className="text-sm text-red-800">
                {formData.isActive ? 'Account is currently active' : 'Account is currently deactivated'}
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
            </label>
          </div>
        </div>

        {/* Audit Log Section */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-custom-text mb-4">
            Activity History (Audit Log)
          </h3>
          {auditLogLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-custom-primary" />
              <span className="ml-2 text-sm text-custom-text-secondary">Loading activity history...</span>
            </div>
          ) : auditLog.length === 0 ? (
            <div className="text-center py-8 text-custom-text-secondary">
              <p>No activity history found for this member.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {auditLog.map((activity, index) => {
                const fieldName = activity.details?.fieldDisplayName || activity.details?.field || '';
                const oldValue = activity.details?.oldValueDisplay || activity.details?.oldValue;
                const newValue = activity.details?.newValueDisplay || activity.details?.newValue;
                const hasChanges = oldValue !== undefined && newValue !== undefined && oldValue !== newValue;
                
                // Format activity type for display
                const getActivityTitle = () => {
                  if (activity.activityType === 'ACCOUNT_CREATED') {
                    return 'User Signed Up';
                  }
                  if (activity.activityType === 'LOGIN_SUCCESS') {
                    return 'User Logged In';
                  }
                  if (activity.activityType === 'ONBOARDING_COMPLETED') {
                    return 'Onboarding Completed';
                  }
                  if (activity.activityType === 'APPLICATION_SUBMITTED' || activity.activityType === 'APPLICATION_RECEIVED') {
                    return 'Application Submitted';
                  }
                  if (activity.activityType === 'APPLICATION_STATUS_CHANGED') {
                    return 'Application Status Changed';
                  }
                  if (activity.activityType === 'ADDED_TO_ROSTER' || activity.activityType === 'MEMBER_ADDED_TO_ROSTER' || activity.activityType === 'RESOURCE_ASSIGNED') {
                    return activity.details?.field === 'roster' ? 'Member Added to Roster' : 'Resource Assigned';
                  }
                  if (activity.activityType === 'ROSTER_CREATED' || activity.activityType === 'ENTITY_CREATED') {
                    return activity.details?.field === 'roster' ? 'Roster Created' : 'Entity Created';
                  }
                  if (activity.activityType === 'ENTITY_REMOVED') {
                    return activity.details?.field === 'roster' ? 'Member Removed from Roster' : 'Entity Removed';
                  }
                  if (activity.activityType === 'DATA_ACTION') {
                    if (activity.details?.action === 'archived') {
                      return 'Roster Archived';
                    }
                    if (activity.details?.action === 'exported') {
                      return 'Roster Exported';
                    }
                    return 'Data Action';
                  }
                  if (activity.activityType === 'SETTING_TOGGLED') {
                    return activity.details?.field === 'duesStatus' ? 'Dues Status Changed' : 'Setting Changed';
                  }
                  if (activity.activityType === 'COMMUNICATION_SENT') {
                    return 'Invitation Sent';
                  }
                  if (activity.activityType === 'PASSWORD_CHANGED') {
                    return 'Password Changed';
                  }
                  if (activity.activityType === 'PASSWORD_RESET_REQUESTED') {
                    return 'Password Reset Requested';
                  }
                  if (activity.activityType === 'PASSWORD_RESET_COMPLETED') {
                    return 'Password Reset Completed';
                  }
                  if (activity.activityType === 'CREDENTIALS_UPDATED') {
                    return 'Login Credentials Updated';
                  }
                  if (activity.activityType === 'ADMIN_IMPERSONATION') {
                    return activity.details?.action === 'impersonation_completed' 
                      ? 'Admin Impersonation Completed'
                      : 'Admin Impersonation Token Generated';
                  }
                  if (fieldName) {
                    return `${fieldName} Updated`;
                  }
                  return activity.action || activity.activityType || 'Activity';
                };
                
                return (
                  <div key={index} className="bg-gray-50 border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="font-medium text-custom-text">
                          {getActivityTitle()}
                        </div>
                        {activity.details?.context && (
                          <div className="text-xs text-blue-600 mt-1">
                            {activity.details.context}
                          </div>
                        )}
                        {activity.details?.emailList && (
                          <div className="text-xs text-gray-600 mt-1 font-mono">
                            {activity.details.emailList}
                          </div>
                        )}
                        {hasChanges && (
                          <div className="text-sm text-custom-text-secondary mt-2 space-y-1">
                            <div>
                              <span className="font-medium text-gray-700">Changed from:</span>
                              <span className="ml-2 text-red-600 line-through">{String(oldValue)}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Changed to:</span>
                              <span className="ml-2 text-green-600 font-medium">{String(newValue)}</span>
                            </div>
                          </div>
                        )}
                        {!hasChanges && activity.details?.note && (
                          <div className="text-sm text-custom-text-secondary mt-2">
                            {activity.details.note}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-custom-text-secondary whitespace-nowrap ml-4">
                        {new Date(activity.timestamp).toLocaleString()}
                      </div>
                    </div>
                    {activity.actingUserId && typeof activity.actingUserId === 'object' && (
                      <div className="text-xs text-custom-text-secondary mt-2 pt-2 border-t border-gray-200">
                        By: {activity.actingUserId.firstName} {activity.actingUserId.lastName} ({activity.actingUserId.email})
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 pt-4 border-t">
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              className="flex-1"
            >
              Save All Changes
            </Button>
          </div>
          <ImpersonateButton 
            userId={user._id} 
            userEmail={user.email} 
            userName={`${user.firstName} ${user.lastName}`}
            accountType={user.accountType}
          />
        </div>
      </div>
    </Modal>
  );
};

// Camp Edit Modal Component - Full features for System Admin
const CampEditModal: React.FC<{
  camp: Camp;
  onClose: () => void;
  onSave: (camp: Camp) => void;
}> = ({ camp, onClose, onSave }) => {
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState<Camp>(camp);
  const [legacyHometown] = useState<string>(camp.hometown || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [campCategories, setCampCategories] = useState<{_id: string, name: string}[]>([]);
  const [globalPerks, setGlobalPerks] = useState<{_id: string, name: string, icon: string, color: string}[]>([]);
  const [auditLog, setAuditLog] = useState<any[]>([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [promoteLoading, setPromoteLoading] = useState(false);
  const [promoteMessage, setPromoteMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadCategoriesAndPerks();
    loadAuditLog();
  }, [camp._id]);

  const loadAuditLog = async () => {
    try {
      setAuditLogLoading(true);
      const response = await apiService.get(`/admin/camps/${camp._id}/history`);
      setAuditLog(response.activities || []);
    } catch (error) {
      console.error('Error loading audit log:', error);
      setAuditLog([]);
    } finally {
      setAuditLogLoading(false);
    }
  };

  const loadCategoriesAndPerks = async () => {
    try {
      const [categoriesRes, perksRes] = await Promise.all([
        apiService.get('/categories'),
        apiService.get('/perks')
      ]);
      setCampCategories(categoriesRes.categories || []);
      setGlobalPerks(perksRes.perks || []);
    } catch (err) {
      console.error('Failed to load categories and perks:', err);
    }
  };

  const ownerUserId = camp.owner && typeof camp.owner === 'object'
    ? camp.owner._id
    : (typeof camp.owner === 'string' ? camp.owner : null);
  const ownerIsSystemAdmin = camp.owner && typeof camp.owner === 'object'
    ? ((camp.owner as any).isSystemAdmin || (camp.owner.accountType === 'admin' && !(camp.owner as any).campId))
    : false;
  const canPromoteOwner = currentUser?.isSystemAdmin && ownerUserId && ownerUserId !== 'camp-repair-needed' && !ownerIsSystemAdmin;

  const handlePromoteOwner = async () => {
    if (!ownerUserId || ownerUserId === 'camp-repair-needed') return;
    if (!window.confirm('Grant this camp owner full system admin access? They will be able to access the admin dashboard and manage all camps and users.')) return;
    setPromoteMessage(null);
    setPromoteLoading(true);
    try {
      const response = await apiService.post(`/admin/users/${ownerUserId}/promote-to-system-admin`);
      setPromoteMessage({ type: 'success', text: response?.message || 'Camp owner promoted to System Admin.' });
    } catch (err: any) {
      setPromoteMessage({ type: 'error', text: err.response?.data?.message || 'Failed to promote. They may already be a system admin.' });
    } finally {
      setPromoteLoading(false);
    }
  };

  const handleCategoryToggle = (categoryId: string) => {
    const currentCategories = formData.categories || [];
    const updated = currentCategories.includes(categoryId)
      ? currentCategories.filter(id => id !== categoryId)
      : [...currentCategories, categoryId];
    setFormData({ ...formData, categories: updated });
  };

  const handlePerkToggle = (perkId: string) => {
    const currentPerks = formData.selectedPerks || [];
    const existingIndex = currentPerks.findIndex(p => p.perkId === perkId);
    
    if (existingIndex >= 0) {
      // Toggle off
      const updated = currentPerks.filter(p => p.perkId !== perkId);
      setFormData({ ...formData, selectedPerks: updated });
    } else {
      // Toggle on
      const updated = [...currentPerks, { perkId, isOn: true }];
      setFormData({ ...formData, selectedPerks: updated });
    }
  };

  const handleSave = () => {
    // Validate password if provided
    if (newPassword && newPassword !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    
    // Add password to save data if provided
    const saveData = { ...formData };
    if (newPassword && newPassword.length >= 6) {
      (saveData as any).newPassword = newPassword;
    }
    
    onSave(saveData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-h2 font-lato-bold text-custom-text">
              SystemAdmin: Edit Camp
            </h2>
            <Button variant="outline" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-6">
            {/* Password Reset for Camp Account */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-custom-text mb-2">Reset Camp Account Password</h3>
              <p className="text-sm text-gray-600 mb-4">Leave blank to keep current password</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-label font-medium text-custom-text mb-2">
                    New Password
                  </label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                  />
                </div>
                <div>
                  <label className="block text-label font-medium text-custom-text mb-2">
                    Confirm Password
                  </label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                  />
                  {newPassword && confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
                  )}
                </div>
              </div>
            </div>

            {/* Camp owner: Promote to System Admin - always show in camp edit so system admins can promote */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-custom-text mb-2">Camp owner: Promote to System Admin</h3>
              <p className="text-sm text-gray-600 mb-3">
                Grant the camp account owner full system admin access so they can manage all camps and users.
              </p>
              {!currentUser?.isSystemAdmin ? (
                <p className="text-sm text-gray-600">Only system admins can promote camp owners. You are signed in as an admin; if you need system admin rights, ask an existing system admin to promote you from the Users tab.</p>
              ) : ownerUserId && ownerUserId !== 'camp-repair-needed' ? (
                <>
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className="text-sm text-gray-700">
                      Owner: {camp.owner && typeof camp.owner === 'object'
                        ? `${(camp.owner as any).firstName || ''} ${(camp.owner as any).lastName || ''}`.trim() || (camp.owner as any).email
                        : 'Camp account'}
                      {(camp.owner && typeof camp.owner === 'object' && (camp.owner as any).email) && (
                        <span className="text-gray-500"> ({(camp.owner as any).email})</span>
                      )}
                    </span>
                    {ownerIsSystemAdmin && (
                      <span className="text-sm font-medium text-green-700">Already a System Admin</span>
                    )}
                  </div>
                  {promoteMessage && (
                    <div className={`mb-3 p-2 rounded text-sm ${promoteMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {promoteMessage.text}
                    </div>
                  )}
                  {canPromoteOwner && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePromoteOwner}
                      disabled={promoteLoading}
                      className="border-purple-600 text-purple-700 hover:bg-purple-100"
                    >
                      {promoteLoading ? 'Promoting...' : 'Promote to System Admin'}
                    </Button>
                  )}
                </>
              ) : (
                <p className="text-sm text-amber-700">No camp owner linked to this camp yet. Use &quot;Log in as this user&quot; below to repair the owner link (if you have a contact email), or promote the user from the Users tab after they have an account.</p>
              )}
            </div>

            {/* Basic Information */}
            <div className="bg-gray-50 border rounded-lg p-4">
              <h3 className="text-lg font-medium text-custom-text mb-4">Basic Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-label font-medium text-custom-text mb-2">
                    Camp Name
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter camp name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-label font-medium text-custom-text mb-2">
                      Burning Since
                    </label>
                    <Input
                      type="number"
                      value={formData.burningSince || new Date().getFullYear()}
                      onChange={(e) => setFormData({ ...formData, burningSince: parseInt(e.target.value) })}
                      placeholder="Year"
                    />
                  </div>
                  <div>
                    <CityAutocomplete
                      value={toStructuredLocationOrNull(formData.location as StructuredLocation | null | undefined)}
                      onChange={(location) => setFormData({
                        ...formData,
                        location: location
                          ? {
                              ...(formData.location || {}),
                              ...location
                            }
                          : {
                              ...(formData.location || {}),
                              city: undefined,
                              state: undefined,
                              country: undefined,
                              countryCode: undefined,
                              lat: undefined,
                              lng: undefined,
                              placeId: undefined
                            },
                        hometown: location?.city || ''
                      })}
                      label="Location"
                      placeholder="Search and select camp city"
                      legacyValue={legacyHometown}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-label font-medium text-custom-text mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-primary focus:border-transparent resize-none"
                    placeholder="Describe your camp..."
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Categories */}
            <div className="bg-gray-50 border rounded-lg p-4">
              <h3 className="text-lg font-medium text-custom-text mb-4">Camp Categories</h3>
              <div className="grid grid-cols-3 gap-3 max-h-48 overflow-y-auto">
                {campCategories.map((category) => (
                  <label key={category._id} className="flex items-center space-x-2 cursor-pointer hover:bg-white p-2 rounded">
                    <input
                      type="checkbox"
                      checked={formData.categories?.includes(category._id)}
                      onChange={() => handleCategoryToggle(category._id)}
                      className="rounded border-gray-300 text-custom-primary focus:ring-custom-primary"
                    />
                    <span className="text-sm text-custom-text">{category.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Shared Amenities */}
            <div className="bg-gray-50 border rounded-lg p-4">
              <h3 className="text-lg font-medium text-custom-text mb-4">Shared Amenities</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
                {globalPerks.map((perk) => (
                  <label key={perk._id} className="flex items-center space-x-2 cursor-pointer hover:bg-white p-2 rounded">
                    <input
                      type="checkbox"
                      checked={formData.selectedPerks?.some(p => p.perkId === perk._id)}
                      onChange={() => handlePerkToggle(perk._id)}
                      className="rounded border-gray-300 text-custom-primary focus:ring-custom-primary"
                    />
                    <span className="text-sm text-custom-text">{perk.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-50 border rounded-lg p-4">
              <h3 className="text-lg font-medium text-custom-text mb-4">Contact Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-label font-medium text-custom-text mb-2">
                    Contact Email
                  </label>
                  <Input
                    type="email"
                    value={formData.contactEmail || ''}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    placeholder="Enter contact email"
                  />
                </div>
                <div>
                  <label className="block text-label font-medium text-custom-text mb-2">
                    Website
                  </label>
                  <Input
                    value={formData.website || ''}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="Enter website URL"
                  />
                </div>
              </div>
            </div>

            {/* Social Media - Only Facebook and Instagram */}
            <div className="bg-gray-50 border rounded-lg p-4">
              <h3 className="text-lg font-medium text-custom-text mb-4">Social Media</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-label font-medium text-custom-text mb-2">
                    Facebook
                  </label>
                  <Input
                    value={formData.socialMedia?.facebook || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      socialMedia: { ...formData.socialMedia, facebook: e.target.value }
                    })}
                    placeholder="https://facebook.com/yourcamp"
                  />
                </div>
                <div>
                  <label className="block text-label font-medium text-custom-text mb-2">
                    Instagram
                  </label>
                  <Input
                    value={formData.socialMedia?.instagram || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      socialMedia: { ...formData.socialMedia, instagram: e.target.value }
                    })}
                    placeholder="https://instagram.com/yourcamp"
                  />
                </div>
              </div>
            </div>

            {/* Camp Settings */}
            <div className="bg-gray-50 border rounded-lg p-4">
              <h3 className="text-lg font-medium text-custom-text mb-4">Camp Settings</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-custom-text">Make Profile Public</label>
                    <p className="text-xs text-gray-500">Display camp on public discovery page</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.isPubliclyVisible !== false}
                    onChange={(e) => setFormData({ ...formData, isPubliclyVisible: e.target.checked })}
                    className="rounded border-gray-300 text-custom-primary focus:ring-custom-primary"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-custom-text">Accepting Applications</label>
                    <p className="text-xs text-gray-500">Allow applications and show Apply Now button</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.acceptingApplications !== false}
                    onChange={(e) => setFormData({ ...formData, acceptingApplications: e.target.checked })}
                    className="rounded border-gray-300 text-custom-primary focus:ring-custom-primary"
                  />
                </div>
              </div>
            </div>

            {/* Account Deactivation Control */}
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-red-900 mb-1">
                    Camp Status
                  </h3>
                  <p className="text-sm text-red-800">
                    {formData.isActive !== false ? 'Camp is currently active' : 'Camp is currently deactivated'}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isActive !== false}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                </label>
              </div>
            </div>

            {/* Audit Log Section */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-custom-text mb-4">
                Activity History (Audit Log)
              </h3>
              {auditLogLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-custom-primary" />
                  <span className="ml-2 text-sm text-custom-text-secondary">Loading activity history...</span>
                </div>
              ) : auditLog.length === 0 ? (
                <div className="text-center py-8 text-custom-text-secondary">
                  <p>No activity history found for this camp.</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {auditLog.map((activity, index) => {
                    const fieldName = activity.details?.fieldDisplayName || activity.details?.field || '';
                    const oldValue = activity.details?.oldValueDisplay || activity.details?.oldValue;
                    const newValue = activity.details?.newValueDisplay || activity.details?.newValue;
                    const hasChanges = oldValue !== undefined && newValue !== undefined && oldValue !== newValue;
                    
                    // Format activity type for display
                    const getActivityTitle = () => {
                      if (activity.activityType === 'ACCOUNT_CREATED') {
                        return 'User Signed Up';
                      }
                      if (activity.activityType === 'LOGIN_SUCCESS') {
                        return 'User Logged In';
                      }
                      if (activity.activityType === 'ONBOARDING_COMPLETED') {
                        return 'Onboarding Completed';
                      }
                      if (activity.activityType === 'APPLICATION_SUBMITTED' || activity.activityType === 'APPLICATION_RECEIVED') {
                        return 'Application Submitted';
                      }
                      if (activity.activityType === 'APPLICATION_STATUS_CHANGED') {
                        return 'Application Status Changed';
                      }
                      if (activity.activityType === 'ADDED_TO_ROSTER' || activity.activityType === 'MEMBER_ADDED_TO_ROSTER' || activity.activityType === 'RESOURCE_ASSIGNED') {
                        return activity.details?.field === 'roster' ? 'Member Added to Roster' : 'Resource Assigned';
                      }
                      if (activity.activityType === 'ROSTER_CREATED' || activity.activityType === 'ENTITY_CREATED') {
                        return activity.details?.field === 'roster' ? 'Roster Created' : 'Entity Created';
                      }
                      if (activity.activityType === 'ENTITY_REMOVED') {
                        return activity.details?.field === 'roster' ? 'Member Removed from Roster' : 'Entity Removed';
                      }
                      if (activity.activityType === 'DATA_ACTION') {
                        if (activity.details?.action === 'archived') {
                          return 'Roster Archived';
                        }
                        if (activity.details?.action === 'exported') {
                          return 'Roster Exported';
                        }
                        return 'Data Action';
                      }
                      if (activity.activityType === 'SETTING_TOGGLED') {
                        return activity.details?.field === 'duesStatus' ? 'Dues Status Changed' : 'Setting Changed';
                      }
                      if (activity.activityType === 'COMMUNICATION_SENT') {
                        return 'Invitation Sent';
                      }
                      if (activity.activityType === 'PASSWORD_CHANGED') {
                        return 'Password Changed';
                      }
                      if (activity.activityType === 'PASSWORD_RESET_REQUESTED') {
                        return 'Password Reset Requested';
                      }
                      if (activity.activityType === 'PASSWORD_RESET_COMPLETED') {
                        return 'Password Reset Completed';
                      }
                      if (activity.activityType === 'CREDENTIALS_UPDATED') {
                        return 'Login Credentials Updated';
                      }
                      if (activity.activityType === 'ADMIN_IMPERSONATION') {
                        return activity.details?.action === 'impersonation_completed' 
                          ? 'Admin Impersonation Completed'
                          : 'Admin Impersonation Token Generated';
                      }
                      if (fieldName) {
                        return `${fieldName} Updated`;
                      }
                      return activity.action || activity.activityType || 'Activity';
                    };
                    
                    return (
                      <div key={index} className="bg-gray-50 border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="font-medium text-custom-text">
                              {getActivityTitle()}
                            </div>
                            {activity.details?.context && (
                              <div className="text-xs text-blue-600 mt-1">
                                {activity.details.context}
                              </div>
                            )}
                            {activity.details?.emailList && (
                              <div className="text-xs text-gray-600 mt-1 font-mono">
                                {activity.details.emailList}
                              </div>
                            )}
                            {hasChanges && (
                              <div className="text-sm text-custom-text-secondary mt-2 space-y-1">
                                <div>
                                  <span className="font-medium text-gray-700">Changed from:</span>
                                  <span className="ml-2 text-red-600 line-through">{String(oldValue)}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-700">Changed to:</span>
                                  <span className="ml-2 text-green-600 font-medium">{String(newValue)}</span>
                                </div>
                              </div>
                            )}
                            {!hasChanges && activity.details?.note && (
                              <div className="text-sm text-custom-text-secondary mt-2">
                                {activity.details.note}
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-custom-text-secondary whitespace-nowrap ml-4">
                            {new Date(activity.timestamp).toLocaleString()}
                          </div>
                        </div>
                        {activity.actingUserId && typeof activity.actingUserId === 'object' && (
                          <div className="text-xs text-custom-text-secondary mt-2 pt-2 border-t border-gray-200">
                            By: {activity.actingUserId.firstName} {activity.actingUserId.lastName} ({activity.actingUserId.email})
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3 pt-4 border-t">
              <div className="flex gap-3">
                <Button variant="outline" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleSave} className="flex-1">
                  Save Changes
                </Button>
              </div>
              <ImpersonateButton 
                userId={
                  camp.owner && typeof camp.owner === 'object' 
                    ? camp.owner._id 
                    : (typeof camp.owner === 'string' ? camp.owner : 'camp-repair-needed')
                } 
                userEmail={camp.owner && typeof camp.owner === 'object' ? camp.owner.email : camp.contactEmail || ''} 
                userName={camp.name || camp.campName || 'Camp'}
                accountType={camp.owner && typeof camp.owner === 'object' ? camp.owner.accountType : 'camp'}
                campId={camp._id}
                needsRepair={!camp.owner && !!camp.contactEmail}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
